/**
 * 録音情報の表示用電話番号を算出する
 *
 * 優先順位:
 * 1. advisor/customer が紐付け済み → その電話番号を返す
 * 2. 未紐付け → originalFrom/originalTo から推定した電話番号を返す
 * 3. 推定不可 → null を返す
 */

interface GetDisplayPhonesInput {
  advisor: { phone: string } | null;
  customer: { phone: string | null; mobilePhone: string | null } | null;
  originalFrom: string | null;
  originalTo: string | null;
}

interface GetDisplayPhonesResult {
  advisorPhone: string | null;
  customerPhone: string | null;
}

/**
 * knownPhone が originalFrom/originalTo のどちらかと一致する場合、
 * もう一方の電話番号を返す。一致しなければ null。
 */
const getOtherPhone = (knownPhone: string, originalFrom: string | null, originalTo: string | null): string | null => {
  if (originalFrom === null || originalTo === null) {
    return null;
  }
  if (knownPhone === originalFrom) {
    return originalTo;
  }
  if (knownPhone === originalTo) {
    return originalFrom;
  }
  return null;
};

/**
 * customer の電話番号を取得（mobilePhone 優先）
 */
const getCustomerPhone = (customer: { phone: string | null; mobilePhone: string | null } | null): string | null => {
  return customer?.mobilePhone ?? customer?.phone ?? null;
};

/**
 * customer の電話番号から advisor の電話番号を推定
 */
const inferAdvisorPhone = (
  customer: { phone: string | null; mobilePhone: string | null } | null,
  originalFrom: string | null,
  originalTo: string | null
): string | null => {
  if (customer === null) {
    return null;
  }
  const phones = [customer.phone, customer.mobilePhone].filter((p): p is string => p !== null);
  for (const phone of phones) {
    const other = getOtherPhone(phone, originalFrom, originalTo);
    if (other !== null) {
      return other;
    }
  }
  return null;
};

export const getDisplayPhones = (input: GetDisplayPhonesInput): GetDisplayPhonesResult => {
  const { advisor, customer, originalFrom, originalTo } = input;

  // advisor の電話番号を決定
  const advisorPhone = advisor?.phone ?? inferAdvisorPhone(customer, originalFrom, originalTo);

  // customer の電話番号を決定
  const customerPhone = getCustomerPhone(customer) ?? getOtherPhone(advisor?.phone ?? '', originalFrom, originalTo);

  // advisor が null の場合、advisor.phone は '' になるので getOtherPhone は null を返す
  // これは意図通り

  return { advisorPhone, customerPhone };
};

export type { GetDisplayPhonesInput, GetDisplayPhonesResult };
